import { PublicClientApplication } from '@azure/msal-browser';
import { PaginatedResponse, ApiError, ActiveRunsErrorResponse } from '../types/api';
import { apiRequest } from '../config/authConfig';
import { createAuthorizationError, ErrorResponse } from '../types/errors';
import { authProviderService, AuthProviderType } from './authProviderService';
import { demoService } from './demoService';
import { recordNativeDiagnostic } from '../utils/nativeDiagnostics';
import { httpFetch } from '../utils/nativeHttp';

export class ApiService {
  private msalInstance: PublicClientApplication | null = null;
  private readonly baseUrl: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_BACKEND_API_URL || 'https://backend-wrong.coint.ai';
  }

  /**
   * Get the active base URL (demo or regular based on mode)
   */
  private getActiveBaseUrl(): string {
    if (demoService.isDemoModeActive()) {
      return demoService.getDemoBackendUrl();
    }
    return this.baseUrl;
  }

  /**
   * Initialize with MSAL instance
   */
  initialize(msalInstance: PublicClientApplication): void {
    this.msalInstance = msalInstance;
  }

  /**
   * Get the current active auth provider
   */
  private getActiveAuthProvider(): AuthProviderType | null {
    return authProviderService.getActiveProvider();
  }

  /**
   * Validate API URL to prevent token confusion in path parameters
   */
  private validateApiUrl(url: string): void {
    try {
      const urlObj = new URL(url);
      const pathSegments = urlObj.pathname.split('/').filter(Boolean);

      // Check for JWT-like tokens in path segments
      for (const segment of pathSegments) {
        if (segment.includes('.') && segment.split('.').length === 3) {
          console.error('JWT token detected in URL path:', {
            url: url,
            suspiciousSegment: segment.substring(0, 20) + '...'
          });
          throw new Error('Invalid URL: JWT token detected in path parameter');
        }

        // Check for suspiciously long path segments (>100 chars)
        if (segment.length > 100) {
          console.error('Suspiciously long path segment:', {
            url: url,
            segmentLength: segment.length,
            segment: segment.substring(0, 20) + '...'
          });
          throw new Error('Invalid URL: path segment too long');
        }

        // Check for Bearer token prefix
        if (segment.toLowerCase().startsWith('bearer')) {
          console.error('Bearer token detected in URL path:', {
            url: url,
            suspiciousSegment: segment.substring(0, 20) + '...'
          });
          throw new Error('Invalid URL: Bearer token detected in path parameter');
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('URL validation failed');
    }
  }

  /**
   * Get access token for API calls
   * Supports both MSAL (Azure AD) and OIDC (renewmyday.com) providers
   */
  private async getAccessToken(): Promise<string | null> {
    const provider = this.getActiveAuthProvider();

    // If OIDC is active, use OIDC service for token
    if (provider === 'oidc') {
      return this.getOidcAccessToken();
    }

    // Default to MSAL
    return this.getMsalAccessToken();
  }

  /**
   * Get access token from OIDC provider
   */
  private async getOidcAccessToken(): Promise<string | null> {
    try {
      const { oidcService } = await import('./oidcService');
      const token = await oidcService.getAccessToken();
      
      if (!token) {
        console.error('OIDC: No access token available');
        return null;
      }
      
      return token;
    } catch (error) {
      console.error('OIDC: Failed to get access token:', error instanceof Error ? error.message : 'Unknown error');
      return null;
    }
  }

  /**
   * Get access token from MSAL (Azure AD) provider
   */
  private async getMsalAccessToken(): Promise<string | null> {
    if (!this.msalInstance) {
      console.error('MSAL instance not initialized');
      return null;
    }

    const activeAccount = this.msalInstance.getActiveAccount();
    if (!activeAccount) {
      console.error('No active account found');
      return null;
    }

    try {
      const response = await this.msalInstance.acquireTokenSilent({
        scopes: apiRequest.scopes,
        account: activeAccount
      });

      recordNativeDiagnostic('API token acquired (silent)');
      return response.accessToken;
    } catch (error) {
      console.error('Failed to acquire access token silently:', error instanceof Error ? error.message : 'Unknown error');
      recordNativeDiagnostic('API token silent acquisition failed', error);
      
      // Check if this is an interaction required error
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorName = (error as any)?.name || '';
      
      // For interaction_required, login_required, or consent_required errors,
      // try interactive token acquisition
      if (errorMessage.includes('interaction_required') || 
          errorMessage.includes('login_required') ||
          errorMessage.includes('consent_required') ||
          errorName === 'InteractionRequiredAuthError') {
        
        console.warn('Interactive authentication required, attempting loginRedirect...');
        
        try {
          // Trigger interactive login
          await this.msalInstance.loginRedirect({
            scopes: apiRequest.scopes,
            account: activeAccount,
            prompt: 'select_account' // Force account selection to ensure fresh auth
          });
          
          // loginRedirect will cause a page reload, so we won't reach here
          // But TypeScript needs a return statement
          return null;
        } catch (redirectError) {
          console.error('Failed to trigger interactive login:', redirectError);
          return null;
        }
      }
      
      // For other errors, return null (will be handled by caller)
      return null;
    }
  }

  /**
   * Force a fresh access token for the active provider, bypassing any cached token.
   * Used to recover from a backend 401 (stale/expired token) so a request can be
   * retried once. Returns the fresh token, or null if none can be obtained.
   */
  private async forceRefreshAccessToken(): Promise<string | null> {
    const provider = this.getActiveAuthProvider();

    // OIDC: refresh via the backend proxy using the stored refresh token.
    if (provider === 'oidc') {
      try {
        const { oidcService } = await import('./oidcService');
        return await oidcService.forceTokenRefresh();
      } catch (error) {
        console.error('OIDC: Failed to force refresh token:', error instanceof Error ? error.message : 'Unknown error');
        return null;
      }
    }

    // MSAL: force a fresh token from the token endpoint, bypassing the cache.
    if (!this.msalInstance) return null;
    const activeAccount = this.msalInstance.getActiveAccount();
    if (!activeAccount) return null;
    try {
      const response = await this.msalInstance.acquireTokenSilent({
        scopes: apiRequest.scopes,
        account: activeAccount,
        forceRefresh: true,
      });
      return response.accessToken;
    } catch (error) {
      console.error('MSAL: Failed to force refresh token:', error instanceof Error ? error.message : 'Unknown error');
      return null;
    }
  }

  /**
   * Get user ID for x-ms-client-principal-id header
   * Supports both MSAL and OIDC providers
   * For MSAL: Uses 'oid' (Object ID) claim - the unique user identifier in Azure AD
   * For OIDC: Uses 'sub' claim which is set as localAccountId
   */
  private getUserId(): string | null {
    const provider = this.getActiveAuthProvider();

    // For OIDC, we need to get the sub claim synchronously
    // The oidcService stores the current user, so we can access it directly
    if (provider === 'oidc') {
      try {
        // Dynamic import would be async, so we need a different approach
        // The oidcService is a singleton that caches the current user
        // We'll use require to get synchronous access
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const { oidcService } = require('./oidcService');
        const profile = oidcService.getUserProfile();
        // localAccountId in OIDC profile is already set to profile.sub
        return profile?.localAccountId || null;
      } catch {
        return null;
      }
    }

    // Default to MSAL
    if (!this.msalInstance) {
      return null;
    }

    const activeAccount = this.msalInstance.getActiveAccount();
    if (!activeAccount) {
      return null;
    }

    // Use 'oid' (Object ID) claim for MSAL - this is what the backend expects
    // The 'oid' is the unique identifier for the user in Azure AD tenant
    const idTokenClaims = activeAccount.idTokenClaims as { oid?: string; sub?: string } | undefined;
    return idTokenClaims?.oid || activeAccount.localAccountId || activeAccount.homeAccountId || null;
  }

  /**
   * Get current account information from active provider
   * @internal Reserved for future use with OIDC provider
   */
  // @ts-expect-error Reserved for future OIDC support
  private async _getAccountInfoAsync(): Promise<{ email?: string; id?: string } | undefined> {
    const provider = this.getActiveAuthProvider();

    if (provider === 'oidc') {
      const { oidcService } = await import('./oidcService');
      const profile = oidcService.getUserProfile();
      if (profile) {
        return {
          email: profile.email || profile.username,
          id: profile.localAccountId
        };
      }
      return undefined;
    }

    // Default to MSAL
    return this.getAccountInfo();
  }

  /**
   * Get current account information from MSAL (synchronous)
   */
  private getAccountInfo(): { email?: string; id?: string } | undefined {
    if (!this.msalInstance) {
      return undefined;
    }

    const account = this.msalInstance.getActiveAccount();
    if (!account) {
      return undefined;
    }

    return {
      email: account.username || account.name,
      id: account.localAccountId || account.homeAccountId
    };
  }

  /**
   * Handle authorization errors (401/403/404) with proper error context
   */
  private async handleAuthorizationError(response: Response, url: string): Promise<never> {
    let backendError: ErrorResponse;
    
    try {
      backendError = await response.json();
    } catch {
      // Fallback if response body is not JSON
      backendError = {
        error: response.statusText || 'Authorization failed',
        statusCode: response.status
      };
    }

    // Special handling for 404 on UserFacts endpoints
    // 404 for facts means the fact doesn't exist yet, not an authorization error
    if (response.status === 404 && url.includes('/facts/')) {
      const error = new Error(backendError.error || 'Fact not found') as ApiError;
      error.status = 404;
      error.type = 'not_found';
      error.userMessage = 'The requested fact does not exist or has expired.';
      throw error;
    }

    // Get current account info for context
    const accountInfo = this.getAccountInfo();

    // Create structured authorization error
    const authError = createAuthorizationError(
      response.status,
      backendError,
      accountInfo
    );

    // Log the error for monitoring (use console.warn to avoid triggering error overlay)
    console.warn('[ApiService] Authorization error detected:', {
      type: authError.type,
      status: authError.status,
      requiredRole: authError.metadata?.requiredRole,
      originalError: backendError.error
    });

    throw authError;
  }

  /**
   * Handle 429 Too Many Requests errors (Active Runs)
   * This happens when trying to send a message while AI is still processing
   */
  private async handleActiveRunsError(response: Response, url: string): Promise<never> {
    let errorData: ActiveRunsErrorResponse;
    
    try {
      errorData = await response.json();
    } catch {
      // Fallback if response body is not JSON
      const error = new Error('Session is currently processing') as ApiError;
      error.status = 429;
      error.type = 'active_runs';
      error.userMessage = 'AI is still processing. Please wait or cancel the current response.';
      throw error;
    }

    // Create typed error with full context
    const error = new Error(errorData.message || 'Session is currently processing') as ApiError & {
      activeRunsData: ActiveRunsErrorResponse;
    };
    error.status = 429;
    error.type = 'active_runs';
    error.userMessage = 'AI is still processing your previous message. You can wait for it to complete or cancel it.';
    (error as any).activeRunsData = errorData;

    // Log for debugging
    console.warn('Active runs error:', {
      sessionId: errorData.sessionId,
      activeRuns: errorData.activeRuns?.length || 0,
      canCancel: errorData.canCancel,
      url
    });

    throw error;
  }

  /**
   * Make HTTP request with authentication
   */
  private async apiCall<T = any>(
    endpoint: string,
    options: RequestInit = {},
    isRetry = false
  ): Promise<T> {
    const activeBaseUrl = this.getActiveBaseUrl();
    const url = endpoint.startsWith('http') ? endpoint : `${activeBaseUrl}${endpoint}`;

    // Validate URL before making request
    this.validateApiUrl(url);

    // In demo mode, we don't need a token - just use demo user ID
    const isDemoMode = demoService.isDemoModeActive();
    
    let token: string | null = null;
    if (!isDemoMode) {
      token = await this.getAccessToken();
      if (!token) {
        const error = new Error('Unable to acquire access token') as ApiError;
        error.type = 'unauthorized';
        error.userMessage = 'Your session has expired or you are not authorized.';
        throw error;
      }
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>)
    };

    // Add authorization header only when not in demo mode
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Add x-ms-client-principal-id header with user ID
    // In demo mode, use demo user ID; otherwise use JWT sub claim
    const userId = isDemoMode ? demoService.getDemoUserId() : this.getUserId();
    if (userId) {
      headers['x-ms-client-principal-id'] = userId;
    }

    // Add demo_password header for demo mode API calls
    if (isDemoMode) {
      const demoPassword = demoService.getDemoPassword();
      if (demoPassword) {
        headers['demo_password'] = demoPassword;
      }
    }

    try {
      const response = await httpFetch(url, {
        ...options,
        headers
      });

      if (!response.ok) {
        recordNativeDiagnostic('API response not ok', { url, status: response.status });

        // A 401 means the token was rejected (typically stale/expired). Attempt a
        // single silent token refresh and retry the request once before surfacing
        // an error, so a briefly-expired session self-heals instead of prompting
        // the user. Skipped in demo mode and never retried more than once.
        if (response.status === 401 && !isDemoMode && !isRetry) {
          const refreshedToken = await this.forceRefreshAccessToken();
          if (refreshedToken) {
            recordNativeDiagnostic('API retrying after 401 token refresh', { url });
            return this.apiCall<T>(endpoint, options, true);
          }
        }

        // Handle authorization errors (401/403/404) with enhanced error context
        if (response.status === 401 || response.status === 403 || response.status === 404) {
          return this.handleAuthorizationError(response, url);
        }

        // Handle 429 Too Many Requests (Active Runs)
        if (response.status === 429) {
          return this.handleActiveRunsError(response, url);
        }

        // Existing error handling for other status codes
        const error = new Error(`API call failed: ${response.status} ${response.statusText}`) as ApiError;
        error.status = response.status;
        error.statusText = response.statusText;
        error.response = response;

        // Set error type based on status
        if (response.status >= 500) {
          error.type = 'server_error';
          error.userMessage = 'Server error occurred. Please try again later.';
        }

        throw error;
      }

      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        return await response.json() as T;
      } else {
        // Try to parse as JSON first, fallback to text
        const textResponse = await response.text();

        try {
          const jsonResponse = JSON.parse(textResponse);
          return jsonResponse as T;
        } catch (jsonError) {
          return textResponse as any;
        }
      }
    } catch (error) {
      console.error('API request failed:', error);
      recordNativeDiagnostic('API request failed', { url, error: error instanceof Error ? `${error.name}: ${error.message}` : String(error) });
      throw error;
    }
  }

  /**
   * GET request
   */
  async get<T = any>(endpoint: string): Promise<T> {
    return this.apiCall<T>(endpoint, { method: 'GET' });
  }

  /**
   * POST request
   */
  async post<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.apiCall<T>(endpoint, {
      method: 'POST',
      ...(data && { body: JSON.stringify(data) })
    });
  }

  /**
   * PUT request
   */
  async put<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.apiCall<T>(endpoint, {
      method: 'PUT',
      ...(data && { body: JSON.stringify(data) })
    });
  }

  /**
   * DELETE request
   */
  async delete<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.apiCall<T>(endpoint, {
      method: 'DELETE',
      ...(data && { body: JSON.stringify(data) })
    });
  }

  /**
   * PATCH request
   */
  async patch<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.apiCall<T>(endpoint, {
      method: 'PATCH',
      ...(data && { body: JSON.stringify(data) })
    });
  }

  /**
   * Get paginated results
   */
  async getPaginated<T = any>(
    endpoint: string,
    page = 1,
    limit = 10
  ): Promise<PaginatedResponse<T>> {
    const url = `${endpoint}?page=${page}&limit=${limit}`;
    return this.apiCall<PaginatedResponse<T>>(url, { method: 'GET' });
  }

  /**
   * Upload file
   */
  async uploadFile<T = any>(endpoint: string, file: File): Promise<T> {
    const activeBaseUrl = this.getActiveBaseUrl();
    const url = endpoint.startsWith('http') ? endpoint : `${activeBaseUrl}${endpoint}`;
    this.validateApiUrl(url);

    // In demo mode, we don't need a token - just use demo user ID
    const isDemoMode = demoService.isDemoModeActive();

    let token: string | null = null;
    if (!isDemoMode) {
      token = await this.getAccessToken();
      if (!token) {
        const error = new Error('Unable to acquire access token') as ApiError;
        error.type = 'unauthorized';
        error.userMessage = 'Your session has expired or you are not authorized.';
        throw error;
      }
    }

    const formData = new FormData();
    formData.append('file', file);

    const headers: Record<string, string> = {};

    // Add authorization header only when not in demo mode
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Add x-ms-client-principal-id header
    // In demo mode, always add it; otherwise only for local development
    if (isDemoMode || process.env.REACT_APP_IS_LOCAL === 'true') {
      const userId = isDemoMode ? demoService.getDemoUserId() : this.getUserId();
      if (userId) {
        headers['x-ms-client-principal-id'] = userId;
      }
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData
      });

      if (!response.ok) {
        // Handle authorization errors (401/403/404) with enhanced error context
        if (response.status === 401 || response.status === 403 || response.status === 404) {
          return this.handleAuthorizationError(response, url);
        }

        const error = new Error(`File upload failed: ${response.status} ${response.statusText}`) as ApiError;
        error.status = response.status;
        error.statusText = response.statusText;
        error.response = response;

        if (response.status >= 500) {
          error.type = 'server_error';
          error.userMessage = 'Server error occurred. Please try again later.';
        }

        throw error;
      }

      const data = await response.json();
      return data as T;
    } catch (error) {
      console.error('File upload failed:', error);
      throw error;
    }
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    try {
      const activeBaseUrl = this.getActiveBaseUrl();
      const response = await fetch(`${activeBaseUrl}/health`);
      return await response.json();
    } catch (error) {
      console.error('Health check failed:', error);
      throw error;
    }
  }

  /**
   * Issue an authenticated streaming GET and return a Response so callers can
   * read `response.body` (e.g. NDJSON / SSE). On web this uses true streaming
   * fetch. In the native Capacitor shell it routes through native HTTP and
   * returns the completed response body, which keeps NDJSON parsers working
   * without relying on WebView fetch/CORS.
   */
  async streamGet(endpoint: string, init: RequestInit = {}): Promise<Response> {
    const activeBaseUrl = this.getActiveBaseUrl();
    const url = endpoint.startsWith('http') ? endpoint : `${activeBaseUrl}${endpoint}`;
    this.validateApiUrl(url);

    const isDemoMode = demoService.isDemoModeActive();
    let token: string | null = null;
    if (!isDemoMode) {
      token = await this.getAccessToken();
      if (!token) {
        const error = new Error('Unable to acquire access token') as ApiError;
        error.type = 'unauthorized';
        error.userMessage = 'Your session has expired or you are not authorized.';
        throw error;
      }
    }

    const headers: Record<string, string> = {
      Accept: 'application/x-ndjson',
      ...(init.headers as Record<string, string>)
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const userId = isDemoMode ? demoService.getDemoUserId() : this.getUserId();
    if (userId) {
      headers['x-ms-client-principal-id'] = userId;
    }
    if (isDemoMode) {
      const demoPassword = demoService.getDemoPassword();
      if (demoPassword) {
        headers['demo_password'] = demoPassword;
      }
    }

    return httpFetch(url, {
      ...init,
      method: 'GET',
      headers
    });
  }
}

// Export singleton instance
export const apiService = new ApiService();
